// Key/value storage that works on web (localStorage) and native (Capacitor
// Preferences, which survives WebView storage eviction on Android/iOS).
// Also the natural home for future offline practice caches.

import { Preferences } from '@capacitor/preferences';
import { isNative } from './platform';

export interface KeyValueStore {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

const webStore: KeyValueStore = {
  async getItem(key) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  async setItem(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      /* storage full or unavailable (private mode) */
    }
  },
  async removeItem(key) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

const nativeStore: KeyValueStore = {
  async getItem(key) {
    return (await Preferences.get({ key })).value;
  },
  async setItem(key, value) {
    await Preferences.set({ key, value });
  },
  async removeItem(key) {
    await Preferences.remove({ key });
  },
};

export const storage: KeyValueStore = isNative() ? nativeStore : webStore;

/** Adapter for supabase-js auth session persistence. */
export const authStorage = {
  getItem: (key: string) => storage.getItem(key),
  setItem: (key: string, value: string) => storage.setItem(key, value),
  removeItem: (key: string) => storage.removeItem(key),
};

/** Stable anonymous id for pre-login funnel analytics. */
export async function getAnonymousId(): Promise<string> {
  const key = 'examforge.anon_id'; // pre-rename key, kept so existing visitors keep their id
  let id = await storage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    await storage.setItem(key, id);
  }
  return id;
}
